// Harde grens voor schaalvragen in de AI-interviewer.
//
// Het model bewaakt de schaal niet: "tien" op een schaal van 1 tot 5 werd
// gewoon herhaald en bevestigd (2026-09-14). Een antwoord buiten de schaal
// mag niet in QuestionnairesResponses landen — daar rekent elke grafiek mee.
// De backend krijgt de reden terug en Decca vraagt opnieuw.
//
// De grenzen komen uit scaleBoundsFor, die ook getQuestionnaire.js gebruikt:
// zo zijn de schaal die Decca noemt en de schaal die hier geldt dezelfde. Dat
// was eerst een kopie met `ReachStart || 1`, en dan werd een schaal 0–5 voor
// Decca 1–5 en het antwoord 0 afgewezen (testronde 2026-10-15, B2).
// AI-CONTEXT: See docs/features/ai-interviewer-gpt-live.md
// AI-CONTEXT: See docs/pitfalls.md#state-or-fallback-erases-zero

import scaleRange from '../../../helpers/questionnaire/scaleRange';

const DEFAULT_MIN = 1;
const DEFAULT_MAX = 5;

/**
 * De onder- en bovengrens van een schaalvraag. Een grens die als tekst is
 * opgeslagen telt als getal, 0 telt als 0; ontbreekt hij, dan 1 en 5.
 *
 * @param {{ ReachStart?: number|string, ReachEnd?: number|string }} field
 * @returns {{min: number, max: number}}
 */
export const scaleBoundsFor = (field) => {
    const range = scaleRange(field, { fallbackStart: DEFAULT_MIN, fallbackEnd: DEFAULT_MAX });
    return range.length > 0
        ? { min: range[0], max: range[range.length - 1] }
        : { min: DEFAULT_MIN, max: DEFAULT_MAX };
};

/**
 * @typedef {object} ScaleCheck
 * @property {boolean} ok
 * @property {number|string} [value] - Het antwoord, als getal bij een schaalvraag
 * @property {string} [reason] - Waarom het antwoord niet mag; voor de backend, dus in het Nederlands
 */

/**
 * @param {{ Type?: string, ReachStart?: number, ReachEnd?: number }|undefined} field - De vraag uit QuestionnaireFields
 * @param {string|number} input - Het antwoord dat de backend wil opslaan
 * @returns {ScaleCheck}
 */
const validateScaleAnswer = (field, input) => {
    if (!field || field.Type !== 'scale') return { ok: true, value: input };

    const { min, max } = scaleBoundsFor(field);
    const text = String(input ?? '').trim();
    const value = /^-?\d+$/.test(text) ? Number(text) : NaN;

    if (!Number.isInteger(value)) {
        return { ok: false, reason: `'${text}' is geen getal. Deze vraag verwacht een heel getal op een schaal van ${min} tot ${max}. Vraag de respondent om een getal binnen die schaal en sla pas dan op.` };
    }
    if (value < min || value > max) {
        return { ok: false, reason: `${value} ligt buiten de schaal van ${min} tot ${max}. Zeg dat tegen de respondent en vraag om een getal binnen de schaal; sla dit antwoord niet op.` };
    }
    return { ok: true, value };
};

export default validateScaleAnswer;
